/**
 * LoadPresetModal Component
 *
 * Modal for loading a saved layer preset.
 * Shows user presets and global presets in tabs.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faGlobe,
  faUser,
  faTrash,
  faLayerGroup,
  faSyncAlt,
  faLink,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { layerPresetService } from '../../../services/layerPresetService';
import { LayerPreset } from '../../../types/layerPreset';
import { buildPermalinkUrl } from '../../shareMap/shareMapState';
import './LoadPresetModal.scss';
import { useConfirm } from '../../../components/common/AppConfirm';
import AppModal from '../../../components/common/AppModal';

interface LoadPresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPresetSelected: (preset: LayerPreset) => Promise<void>;
  isAdmin?: boolean;
}

export const LoadPresetModal: React.FC<LoadPresetModalProps> = ({
  isOpen,
  onClose,
  onPresetSelected,
  isAdmin = false,
}) => {
  const askConfirm = useConfirm();
  const [activeTab, setActiveTab] = useState<'my' | 'global'>('my');
  const [presets, setPresets] = useState<LayerPreset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPresetId, setLoadingPresetId] = useState<string | null>(null);
  const [deletingPresetId, setDeletingPresetId] = useState<string | null>(null);
  const [copiedPresetId, setCopiedPresetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch presets when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPresets();
    }
  }, [isOpen]);

  const fetchPresets = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await layerPresetService.getPresetsForCurrentUser();
      setPresets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  // Filter presets by tab
  const filteredPresets = presets.filter(preset =>
    activeTab === 'global' ? preset.is_global : !preset.is_global
  );

  const handleLoadPreset = useCallback(async (preset: LayerPreset) => {
    setLoadingPresetId(preset.id);
    setError(null);

    try {
      await onPresetSelected(preset);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preset');
    } finally {
      setLoadingPresetId(null);
    }
  }, [onPresetSelected, onClose]);

  const handleDeletePreset = useCallback(async (preset: LayerPreset, e: React.MouseEvent) => {
    e.stopPropagation();

    // Don't allow deleting global presets unless admin
    if (preset.is_global && !isAdmin) {
      setError('Only administrators can delete global presets');
      return;
    }

    const confirmed = await askConfirm({
      title: 'Delete preset',
      message: `Delete preset "${preset.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) return;

    setDeletingPresetId(preset.id);
    setError(null);

    try {
      await layerPresetService.deletePreset(preset.id);
      setPresets(prev => prev.filter(p => p.id !== preset.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete preset');
    } finally {
      setDeletingPresetId(null);
    }
  }, [isAdmin]);

  // #557 — copy a saved map's permalink straight from its row.
  const handleCopyPermalink = useCallback(async (preset: LayerPreset, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = buildPermalinkUrl(window.location.origin, preset.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedPresetId(preset.id);
      setTimeout(() => setCopiedPresetId(prev => (prev === preset.id ? null : prev)), 2000);
    } catch {
      setError('Could not copy the link to the clipboard');
    }
  }, []);

  if (!isOpen) return null;

  return (
    <AppModal
      isOpen
      onClose={onClose}
      width={520}
      maxHeight="90vh"
      header={<h3>Load Layer Preset</h3>}
    >
      {/* Escape dismissal belongs to AppModal (#968); this wrapper's keydown handler
          only ever called onClose, which is what AppModal now does. */}
      <div className="load-preset-modal">
        <div className="preset-tabs">
          <button
            className={`preset-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>My Presets</span>
          </button>
          <button
            className={`preset-tab ${activeTab === 'global' ? 'active' : ''}`}
            onClick={() => setActiveTab('global')}
          >
            <FontAwesomeIcon icon={faGlobe} />
            <span>Global Presets</span>
          </button>
        </div>

        <div className="load-preset-modal-body">
          {error && (
            <div className="load-preset-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="preset-loading">
              <FontAwesomeIcon icon={faSyncAlt} spin />
              <span>Loading presets...</span>
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="preset-empty">
              <FontAwesomeIcon icon={faLayerGroup} />
              <span>
                {activeTab === 'my'
                  ? "You haven't saved any presets yet."
                  : 'No global presets available.'}
              </span>
            </div>
          ) : (
            <div className="preset-list">
              {filteredPresets.map(preset => (
                <div
                  key={preset.id}
                  className={`preset-item ${loadingPresetId === preset.id ? 'loading' : ''}`}
                  onClick={() => handleLoadPreset(preset)}
                >
                  <div className="preset-item-icon">
                    <FontAwesomeIcon icon={preset.is_global ? faGlobe : faUser} />
                  </div>
                  <div className="preset-item-content">
                    <div className="preset-item-name">{preset.name}</div>
                    {preset.description && (
                      <div className="preset-item-description">{preset.description}</div>
                    )}
                    <div className="preset-item-meta">
                      <span className="layer-count">
                        {preset.preset_data.layers.length} layers
                        {preset.preset_data.liveLayers.length > 0 &&
                          ` + ${preset.preset_data.liveLayers.length} live`}
                      </span>
                    </div>
                  </div>
                  <div className="preset-item-actions">
                    {loadingPresetId === preset.id ? (
                      <div className="loading-indicator">
                        <FontAwesomeIcon icon={faSyncAlt} spin />
                      </div>
                    ) : (
                      <>
                        <button
                          className="action-btn link-btn"
                          aria-label="Copy link"
                          title={`${buildPermalinkUrl(window.location.origin, preset.id)} (click to copy link)`}
                          onClick={(e) => handleCopyPermalink(preset, e)}
                        >
                          <FontAwesomeIcon icon={copiedPresetId === preset.id ? faCheck : faLink} />
                        </button>
                        <button
                          className="action-btn load-btn"
                          title="Load preset"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadPreset(preset);
                          }}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </button>
                        {(!preset.is_global || isAdmin) && (
                          <button
                            className="action-btn delete-btn"
                            title="Delete preset"
                            onClick={(e) => handleDeletePreset(preset, e)}
                            disabled={deletingPresetId === preset.id}
                          >
                            {deletingPresetId === preset.id ? (
                              <FontAwesomeIcon icon={faSyncAlt} spin />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} />
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="load-preset-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </AppModal>
  );
};
